import { useEffect, useMemo, useState } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { apiFetch } from "../utils/apiClient";
import { calcDynamicBodyFat, calcDynamicLeanMass } from "../utils/calculations";
import { fmtDate } from "../utils/formatting";
import { btn } from "../styles/shared";

const AUTO_URL = process.env.NODE_ENV === "development"
  ? "http://localhost:8788/api/autobiography"
  : "/api/autobiography";

function dateLabel(date) {
  return date ? `${fmtDate(date)} (${date})` : "Unknown date";
}

function photoWarning(entry) {
  const warnings = entry.photoMetadata?.metadataWarnings || [];
  if (entry.photoDate && entry.photoDate !== entry.date) return `Photo metadata says ${entry.photoDate}, logged as ${entry.date}`;
  if (warnings.length) return warnings[0];
  if (!entry.photoDateConfidence) return "Photo date not verified";
  return "";
}

function metricDelta(a, b, key, suffix = "") {
  const left = Number(a?.[key]);
  const right = Number(b?.[key]);
  if (!Number.isFinite(left) || !Number.isFinite(right)) return "-";
  const delta = Math.round((right - left) * 100) / 100;
  return `${delta > 0 ? "+" : ""}${delta}${suffix}`;
}

export default function History({ weightLog = [], fitnessGoals = {} }) {
  const [autobiography, setAutobiography] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [leftDate, setLeftDate] = useState("");
  const [rightDate, setRightDate] = useState("");

  useEffect(() => {
    apiFetch(AUTO_URL)
      .then(response => response.json())
      .then(data => setAutobiography(Array.isArray(data) ? data : []))
      .catch(() => setAutobiography([]));
  }, []);

  const photos = useMemo(() => [...weightLog]
    .filter(entry => entry.photoUrl)
    .sort((a, b) => b.date.localeCompare(a.date))
    .map(entry => {
      const bodyFat = calcDynamicBodyFat(entry.weight, fitnessGoals, weightLog);
      return {
        ...entry,
        bodyFat,
        leanMass: calcDynamicLeanMass(entry.weight, fitnessGoals, weightLog),
      };
    }), [weightLog, fitnessGoals]);

  useEffect(() => {
    if (!photos.length) return;
    if (!leftDate) setLeftDate(photos[Math.min(1, photos.length - 1)]?.date || photos[0].date);
    if (!rightDate) setRightDate(photos[0].date);
  }, [photos, leftDate, rightDate]);

  const left = photos.find(entry => entry.date === leftDate);
  const right = photos.find(entry => entry.date === rightDate);
  const chartData = [...weightLog]
    .sort((a, b) => a.date.localeCompare(b.date))
    .map(entry => ({
      date: entry.date.slice(5),
      weight: Number(entry.weight),
      bodyFat: calcDynamicBodyFat(entry.weight, fitnessGoals, weightLog),
      leanMass: calcDynamicLeanMass(entry.weight, fitnessGoals, weightLog),
    }));

  return (
    <div style={{ display:"flex",flexDirection:"column",gap:18 }}>
      <div>
        <h1 style={{ fontSize:26,fontWeight:600,margin:0,letterSpacing:-0.5 }}>History</h1>
        <p style={{ color:"#999",margin:"4px 0 0",fontSize:14 }}>Body photos, date verification, and long-range progress</p>
      </div>

      {photos.length >= 2 && (
        <div style={{ background:"linear-gradient(135deg,#06151c,#050c12)",border:"1px solid rgba(34,211,238,0.18)",borderRadius:16,padding:16 }}>
          <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,flexWrap:"wrap",marginBottom:12 }}>
            <div>
              <div style={{ fontSize:11,fontFamily:"'Space Mono',monospace",color:"#22d3ee",textTransform:"uppercase",letterSpacing:1 }}>Compare Photos</div>
              <div style={{ fontSize:12,color:"#64748b",marginTop:3 }}>Select two verified weigh-ins and inspect them side by side.</div>
            </div>
            <div style={{ display:"flex",gap:8,flexWrap:"wrap" }}>
              {[["From", leftDate, setLeftDate], ["To", rightDate, setRightDate]].map(([label, value, setter]) => (
                <label key={label} style={{ display:"flex",flexDirection:"column",gap:4,fontSize:10,color:"#64748b",fontFamily:"'Space Mono',monospace" }}>
                  {label}
                  <select value={value} onChange={event => setter(event.target.value)}
                    style={{ background:"#07131a",color:"#cbd5e1",border:"1px solid rgba(34,211,238,0.2)",borderRadius:8,padding:"8px 10px" }}>
                    {photos.map(entry => <option key={entry.date} value={entry.date}>{entry.date} · {entry.weight}kg</option>)}
                  </select>
                </label>
              ))}
            </div>
          </div>

          <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))",gap:12 }}>
            {[left, right].filter(Boolean).map(entry => (
              <button key={entry.date} onClick={() => setSelectedImage(entry)} style={{ padding:0,textAlign:"left",background:"rgba(255,255,255,0.03)",border:"1px solid rgba(34,211,238,0.14)",borderRadius:12,overflow:"hidden",cursor:"pointer" }}>
                <img src={entry.photoUrl} alt={`weigh-in ${entry.date}`} style={{ width:"100%",height:300,objectFit:"cover",display:"block" }} />
                <div style={{ padding:12 }}>
                  <div style={{ color:"#e2e8f0",fontWeight:700 }}>{dateLabel(entry.date)}</div>
                  <div style={{ color:"#94a3b8",fontSize:12,marginTop:4 }}>{entry.weight}kg{entry.bodyFat != null ? ` · BF ${entry.bodyFat}%` : ""}{entry.leanMass ? ` · LBM ${entry.leanMass}kg` : ""}</div>
                  <div style={{ color:entry.photoDate === entry.date ? "#22c55e" : "#fbbf24",fontSize:11,marginTop:6 }}>
                    Photo date: {entry.photoDate || "unverified"} · {entry.photoDateSource || "app_entry"} · {entry.photoDateConfidence || "low"}
                  </div>
                </div>
              </button>
            ))}
          </div>

          {left && right && (
            <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(130px,1fr))",gap:8,marginTop:12 }}>
              {[
                ["Weight change", metricDelta(left, right, "weight", "kg"), "#a78bfa"],
                ["Lean mass change", metricDelta(left, right, "leanMass", "kg"), "#22d3ee"],
                ["Days apart", `${Math.round((new Date(right.date) - new Date(left.date)) / 86400000)}d`, "#94a3b8"],
              ].map(([label, value, color]) => (
                <div key={label} style={{ background:"rgba(255,255,255,0.03)",borderRadius:10,padding:"10px 12px" }}>
                  <div style={{ fontSize:9,color:"#64748b",fontFamily:"'Space Mono',monospace",textTransform:"uppercase",letterSpacing:0.8 }}>{label}</div>
                  <div style={{ fontSize:18,color,fontFamily:"'Space Mono',monospace",fontWeight:700,marginTop:4 }}>{value}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {chartData.length > 1 && (
        <div style={{ background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:16,padding:16 }}>
          <div style={{ fontSize:11,color:"#94a3b8",fontFamily:"'Space Mono',monospace",letterSpacing:1,marginBottom:14 }}>WEIGHT AND LEAN MASS</div>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)"/>
              <XAxis dataKey="date" tick={{fill:"#64748b",fontSize:10}} axisLine={false} tickLine={false}/>
              <YAxis tick={{fill:"#64748b",fontSize:10}} axisLine={false} tickLine={false} width={42} domain={["auto","auto"]}/>
              <Tooltip contentStyle={{background:"#0f0f1a",border:"1px solid rgba(255,255,255,0.1)",borderRadius:10,color:"#e2e8f0",fontSize:12}}/>
              <Line type="monotone" dataKey="weight" stroke="#a78bfa" strokeWidth={2.5} dot={{r:4,fill:"#a78bfa",strokeWidth:0}} name="Weight (kg)" connectNulls/>
              <Line type="monotone" dataKey="leanMass" stroke="#22d3ee" strokeWidth={1.8} dot={false} name="LBM (kg)" connectNulls strokeDasharray="4 3"/>
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      <div style={{ background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:16,padding:16 }}>
        <div style={{ fontSize:11,color:"#94a3b8",fontFamily:"'Space Mono',monospace",letterSpacing:1,marginBottom:12 }}>PHOTO AUDIT TRAIL</div>
        {photos.length ? (
          <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(150px,1fr))",gap:10 }}>
            {photos.map(entry => {
              const warning = photoWarning(entry);
              return (
                <button key={entry.date} onClick={() => setSelectedImage(entry)}
                  style={{ background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.08)",borderRadius:12,overflow:"hidden",padding:0,textAlign:"left",cursor:"pointer" }}>
                  <img src={entry.photoUrl} alt={`weigh-in ${entry.date}`} style={{ width:"100%",aspectRatio:"1/1",objectFit:"cover",display:"block" }}/>
                  <div style={{ padding:10 }}>
                    <div style={{ color:"#e2e8f0",fontSize:12,fontWeight:700 }}>{entry.date}</div>
                    <div style={{ color:"#a78bfa",fontSize:12,marginTop:2 }}>{entry.weight}kg</div>
                    <div style={{ color:warning ? "#fbbf24" : "#22c55e",fontSize:10,marginTop:5,lineHeight:1.35 }}>
                      {warning || `Verified by ${entry.photoDateSource}`}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        ) : (
          <div style={{ color:"#64748b",fontSize:13,padding:"20px 0" }}>No weigh-in photos have been saved yet.</div>
        )}
      </div>

      <div style={{ background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:16,padding:16 }}>
        <div style={{ fontSize:11,color:"#94a3b8",fontFamily:"'Space Mono',monospace",letterSpacing:1,marginBottom:12 }}>NARRATIVE SNAPSHOTS</div>
        {autobiography === null ? (
          <div style={{ color:"#64748b",fontSize:13 }}>Loading history...</div>
        ) : autobiography.length ? (
          <div style={{ display:"flex",flexDirection:"column",gap:10 }}>
            {[...autobiography].sort((a,b) => b.date.localeCompare(a.date)).slice(0, 12).map(snapshot => (
              <div key={snapshot.date} style={{ background:"rgba(255,255,255,0.025)",border:"1px solid rgba(255,255,255,0.06)",borderRadius:12,padding:12 }}>
                <div style={{ display:"flex",justifyContent:"space-between",gap:10,flexWrap:"wrap" }}>
                  <div style={{ color:"#22d3ee",fontFamily:"'Space Mono',monospace",fontSize:13 }}>{snapshot.date}</div>
                  <div style={{ color:"#94a3b8",fontSize:12 }}>Weight {snapshot.weight || "-"}kg · BF {snapshot.bodyFat ?? "-"}%</div>
                </div>
                {snapshot.photoDate && (
                  <div style={{ color:snapshot.photoDate === snapshot.date ? "#22c55e" : "#fbbf24",fontSize:11,marginTop:5 }}>
                    Snapshot photo date: {snapshot.photoDate} · {snapshot.photoDateSource || "unknown"} · {snapshot.photoDateConfidence || "low"}
                  </div>
                )}
                {snapshot.insights?.length > 0 && (
                  <div style={{ marginTop:8,color:"#94a3b8",fontSize:12,lineHeight:1.5 }}>{snapshot.insights[0]}</div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div style={{ color:"#64748b",fontSize:13 }}>No narrative snapshots yet. They are generated by backups.</div>
        )}
      </div>

      {selectedImage && (
        <div onClick={() => setSelectedImage(null)}
          style={{ position:"fixed",inset:0,zIndex:1000,background:"rgba(0,0,0,0.88)",display:"grid",placeItems:"center",padding:18 }}>
          <div onClick={event => event.stopPropagation()} style={{ maxWidth:900,width:"100%",maxHeight:"92vh",display:"flex",flexDirection:"column",gap:10 }}>
            <img src={selectedImage.photoUrl} alt={`weigh-in ${selectedImage.date}`} style={{ maxWidth:"100%",maxHeight:"78vh",objectFit:"contain",borderRadius:12,display:"block",margin:"0 auto" }}/>
            <div style={{ background:"#071015",border:"1px solid rgba(255,255,255,0.1)",borderRadius:12,padding:12 }}>
              <div style={{ color:"#e2e8f0",fontWeight:700 }}>{selectedImage.date} · {selectedImage.weight}kg</div>
              <div style={{ color:"#94a3b8",fontSize:12,marginTop:4 }}>
                Photo taken: {selectedImage.photoDate || "unverified"} · source {selectedImage.photoDateSource || "app_entry"} · confidence {selectedImage.photoDateConfidence || "low"}
              </div>
              {photoWarning(selectedImage) && <div style={{ color:"#fbbf24",fontSize:12,marginTop:5 }}>{photoWarning(selectedImage)}</div>}
              <button onClick={() => setSelectedImage(null)} style={{ ...btn,marginTop:10,padding:"7px 12px",fontSize:12,color:"#22d3ee",border:"1px solid rgba(34,211,238,0.25)",background:"rgba(34,211,238,0.08)" }}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
